import { ApolloQueryResult } from '@apollo/client';
import { Box, Flex, Grid, GridItem, VStack } from '@televet/kibble-ui/build/chakra';
import {
  Modal,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooterButtons,
} from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { useTracking } from 'shared/providers/TrackingProvider';
import { addDays, differenceInBusinessDays, differenceInCalendarDays } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { formatTimesStringToDateStringWithTimeZone } from 'pages/Settings/pages/AppointmentSettings/components/DirectBookingSettings/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import { ReportingPaymentsReportEvents } from 'shared/providers/TrackingProvider/EventNames';
import {
  Exact,
  FindFirstPayoutBatchingPeriodQuery,
  PayoutBatchingPeriodWhereInput,
  useCreateOnePayoutBatchingPeriodMutation,
  useFindMissingPayoutBatchingPeriodDatesQuery,
} from 'shared/types/graphql';
import PayoutSwitch from './PayoutSwitch';
import { Button } from '@televet/kibble-ui/build/components/Button';

interface isEODModalProps {
  onClose: () => void;
  isOpen: boolean;
  pimsName: string;
  previousEndsAt: string;
  clinicId?: string;
  clinicUserId?: string;
  refreshUI: (
    variables?:
      | Partial<
          Exact<{
            where?: PayoutBatchingPeriodWhereInput | null | undefined;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<FindFirstPayoutBatchingPeriodQuery>>;
}

/**
 * This component displays a modal for setting "End of Day" (EOD) for a given clinic.
 *
 * The modal allows the user to select one or more payout dates that are missing an EOD entry.
 * Each date can be toggled on or off, determining whether an EOD entry will be created for that date.
 *
 * When the user submits the form, GraphQL mutations are invoked for each selected date to create the EOD entries.
 * Each entry contains:
 *  - startsAt: The starting date and time for the payout period
 *  - endsAt: The ending date and time for the payout period
 *  - clinic: The ID of the clinic for which the EOD is being set
 *
 * The time for each "endsAt" field is provided by the user and defaults to the current time.
 * The "startsAt" field for the first entry is set to the "previousEndsAt" prop, while for subsequent entries, it is set to the "endsAt" field of the previous entry.
 *
 */

const EODModal = ({
  onClose,
  isOpen,
  pimsName,
  previousEndsAt,
  clinicId,
  clinicUserId,
  refreshUI,
}: isEODModalProps): JSX.Element => {
  const toast = useToast();
  const [selectedDates, setSelectedDates] = useState<{ [date: string]: string }>({});

  const { data: missingPayoutBatchingPeriodDatesData, refetch: refreshMissingPayoutBatchingPeriodData } =
    useFindMissingPayoutBatchingPeriodDatesQuery({
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });
  const { trackEvent } = useTracking();

  // an array of strings that represent the dates that are missing a payout batching period, ie. ['2021-08-01', '2021-08-02', '2021-08-03']
  const dates = useMemo(() => {
    return missingPayoutBatchingPeriodDatesData?.findMissingPayoutBatchingPeriodDates?.dates || [];
  }, [missingPayoutBatchingPeriodDatesData?.findMissingPayoutBatchingPeriodDates]);

  const currentBatchingPeriod = useMemo(() => (!!dates.length ? dates[0] : undefined), [dates]);

  const previousBatchingPeriods = useMemo(() => {
    if (!dates.length) {
      return [];
    }
    return dates.slice(1);
  }, [dates]);

  const areAllPreviousBatchingPeriodsSelected = useMemo(() => {
    return previousBatchingPeriods.every((date) => selectedDates[date]);
  }, [previousBatchingPeriods, selectedDates]);

  useEffect(() => {
    if (dates.length > 0 && isOpen) {
      // set selectedDates to an object with each date from the dates array as a key and the current time as the value
      const current = new Date();
      setSelectedDates(
        dates.reduce((acc, date) => {
          const day = utcToZonedTime(date, 'UTC');
          const dayAtCurrentTime = new Date(day).setHours(current.getHours(), current.getMinutes());
          return {
            ...acc,
            [date]: new Date(dayAtCurrentTime).toISOString(),
          };
        }, {}),
      );
    }
  }, [dates, isOpen]);

  const updateBatchingPeriod = useCallback(
    ({ endsAt, time, isChecked }: { endsAt: string; time: Date; isChecked: boolean }) => {
      if (isChecked) {
        setSelectedDates((prev) => ({ ...prev, [endsAt]: time.toISOString() }));
      } else {
        setSelectedDates((prev) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [endsAt]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [],
  );

  const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false);

  const [createOnePayoutBatchingPeriod] = useCreateOnePayoutBatchingPeriodMutation();

  const handleClose = useCallback(() => {
    trackEvent(ReportingPaymentsReportEvents.PayEodCancel);

    if (dates.length > 0) {
      // set selectedDates to an object with each date from the dates array as a key and the current time as the value again on close to reset state in the modal
      setSelectedDates(dates.reduce((acc, date) => ({ ...acc, [date]: new Date().toISOString() }), {}));
    }
    onClose();
  }, [dates, onClose, trackEvent]);

  const onSubmit = useCallback(() => {
    //create the data object for the mutation based on the selectedDates object
    setIsSubmitButtonLoading(true);
    let formattedPreviousEndsAt = new Date(previousEndsAt).toISOString(); // Format the date to ISO string
    Object.entries(selectedDates)
      .sort((prev, next) => {
        const prevTime = new Date(prev[0]).getTime();
        const nextTime = new Date(next[0]).getTime();
        return prevTime > nextTime ? 1 : nextTime > prevTime ? -1 : 0;
      })
      .forEach(([date, time], index) => {
        const data = {
          startsAt: formattedPreviousEndsAt,
          endsAt: time,
          createdByUser: { connect: { id: clinicUserId } },
          clinic: { connect: { id: clinicId } },
        };

        formattedPreviousEndsAt = data.endsAt;

        setSelectedDates((prev) => {
          return {
            ...prev,
            [date]: data.endsAt,
          };
        });

        // these methods remove the time from the date, so need to add 1 day to get accurate results since we go end of day to end of day, not start to start
        const numberOfWeekdays = differenceInBusinessDays(
          addDays(new Date(data.endsAt), 1),
          addDays(new Date(data.startsAt), 1),
        );
        const numberOfDays = differenceInCalendarDays(
          addDays(new Date(data.endsAt), 1),
          addDays(new Date(data.startsAt), 1),
        );

        trackEvent(ReportingPaymentsReportEvents.PayEodBatchCreate, {
          numberOfWeekdays,
          numberOfDays,
          numberOfBatches: Object.keys(selectedDates).length,
        });

        createOnePayoutBatchingPeriod({
          variables: { data },

          onCompleted: (responseData) => {
            toast({
              description: `End of Day successfully set for ${formatTimesStringToDateStringWithTimeZone(
                responseData.createOnePayoutBatchingPeriod.endsAt,
              )}!`,
              status: 'success',
              duration: 5000,
            });

            // if it is the last iteration, close the modal
            if (index === Object.entries(selectedDates).length - 1) {
              setIsSubmitButtonLoading(false);
              refreshMissingPayoutBatchingPeriodData();
              refreshUI();
              onClose();
            }
          },
          onError: (error) => {
            // Handle error scenario
            setIsSubmitButtonLoading(false); // Reset loading state on error
            toast({
              description: `Error: ${error.message}`,
              status: 'error',
              duration: 5000,
            });
          },
        });
      });
  }, [
    previousEndsAt,
    selectedDates,
    clinicUserId,
    clinicId,
    trackEvent,
    createOnePayoutBatchingPeriod,
    toast,
    refreshMissingPayoutBatchingPeriodData,
    refreshUI,
    onClose,
  ]);

  const isSubmitButtonDisabled = useMemo(() => {
    // if selectedDates has no entries
    return Object.keys(selectedDates).length === 0;
  }, [selectedDates]);

  const handleClearAll = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    setSelectedDates((prev) => {
      return !!prev[today] ? { [today]: prev[today] } : {};
    });
  }, []);

  const getInitialTime = useCallback(
    (date: string) => {
      if (selectedDates[date]) {
        return new Date(selectedDates[date]);
      }

      const current = new Date();
      const day = utcToZonedTime(date, 'UTC');
      const dayAtCurrentTime = new Date(day).setHours(current.getHours(), current.getMinutes());
      return new Date(dayAtCurrentTime);
    },
    [selectedDates],
  );

  const handleSelectAll = useCallback(() => {
    const newSelectedDates = { ...selectedDates };
    previousBatchingPeriods.forEach((date) => {
      if (!selectedDates[date]) {
        newSelectedDates[date] = getInitialTime(date).toISOString();
      }
    });
    setSelectedDates(newSelectedDates);
  }, [getInitialTime, previousBatchingPeriods, selectedDates]);

  const handleApplyTimeToAllDays = useCallback((updateTime: Date) => {
    setSelectedDates((prev) => {
      const entries = Object.entries(prev);
      return entries.reduce((acc, [date, time]) => {
        const today = new Date().toISOString().split('T')[0];
        if (date.startsWith(today)) {
          return { ...acc, [date]: time };
        }
        return { ...acc, [date]: updateTime.toISOString() };
      }, {});
    });
  }, []);

  return (
    <Modal onClose={handleClose} isOpen={isOpen} scrollBehavior="inside" closeOnOverlayClick={false}>
      <ModalHeader>End Day</ModalHeader>
      <ModalCloseButton />
      <ModalBody pb={5}>
        <Text as="p" mb={6}>
          {`Please select the end of day time that matches ${pimsName} for any relevant payout dates.`}
        </Text>
        <Box>
          <Text fontWeight="semibold">Today</Text>
          <VStack
            className="Schedules"
            bgColor="background.secondaryAlpha"
            p={3}
            borderRadius="md"
            gap={2}
            alignItems="flex-start"
            mt={2}
          >
            <Grid className="Schedules__Header" templateColumns="repeat(6, 1fr)" w="100%" gap={4}>
              <GridItem colSpan={3}>
                <Text variant="subtle" textTransform="uppercase" size="xs">
                  Day
                </Text>
              </GridItem>
              <GridItem colSpan={2}>
                <Popover
                  trigger="hover"
                  anchor={
                    <Box>
                      <Text variant="subtle" textTransform="uppercase" size="xs">
                        End Time
                      </Text>
                    </Box>
                  }
                  placement="top"
                  size="lg"
                >
                  <Text as="p" size="sm" variant="onContrast" fontWeight="semibold">
                    The cutoff for this day&apos;s transactions.{' '}
                    <Text as="span" size="sm" variant="onContrast" fontWeight="normal">
                      Payments after this time will appear in the next day&apos;s report.
                    </Text>
                  </Text>
                </Popover>
              </GridItem>
              <GridItem>
                <Popover
                  trigger="hover"
                  anchor={
                    <Box>
                      <Text variant="subtle" textTransform="uppercase" size="xs">
                        Payout Day
                      </Text>
                    </Box>
                  }
                  placement="top"
                  size="lg"
                >
                  <Text as="p" size="sm" variant="onContrast" fontWeight="semibold">
                    Marks this date as the end of a reporting period.{' '}
                    <Text as="span" size="sm" variant="onContrast" fontWeight="normal">
                      Transactions after this date&apos;s end time will be included in the next Payout Day&apos;s
                      report.
                    </Text>
                  </Text>
                </Popover>
              </GridItem>
            </Grid>
            {currentBatchingPeriod && (
              <PayoutSwitch
                endsAt={currentBatchingPeriod}
                initialTime={getInitialTime(currentBatchingPeriod)}
                updateBatchingPeriod={updateBatchingPeriod}
                isToday={true}
                isSelected={!!selectedDates[currentBatchingPeriod]}
              />
            )}
          </VStack>
        </Box>
        {!!previousBatchingPeriods.length && (
          <Box mt={6}>
            <Flex alignItems="center" justifyContent="space-between">
              <Text fontWeight="semibold">Previous Unclosed Days</Text>
              {areAllPreviousBatchingPeriodsSelected ? (
                <Button variant="ghostNeutral" iconName="arrowCircle" size="xs" onClick={handleClearAll}>
                  Clear All
                </Button>
              ) : (
                <Button variant="ghost" size="xs" onClick={handleSelectAll}>
                  Select All
                </Button>
              )}
            </Flex>
            <VStack
              className="Schedules"
              bgColor="background.secondaryAlpha"
              p={3}
              borderRadius="md"
              gap={2}
              alignItems="flex-start"
              mt={2}
            >
              {previousBatchingPeriods.map((date, index) => {
                const initialTime = getInitialTime(date);
                return (
                  <PayoutSwitch
                    key={index}
                    endsAt={date}
                    initialTime={initialTime}
                    updateBatchingPeriod={updateBatchingPeriod}
                    handleApplyTimeToAllDays={handleApplyTimeToAllDays}
                    isSelected={!!selectedDates[date]}
                  />
                );
              })}
            </VStack>
          </Box>
        )}
      </ModalBody>
      <ModalFooterButtons
        onCancel={handleClose}
        onSubmit={onSubmit}
        isLoading={isSubmitButtonLoading}
        isDisabled={isSubmitButtonDisabled}
      />
    </Modal>
  );
};

export default EODModal;
